import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Table, Button, Select, Space, message } from "antd";
import { ReloadOutlined, StopOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency } from "../../../utils/formatters";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { formatRangeParams, resolveDatePreset } from "../../../shared/lib/dateFilterPresets";
import { orderDocNo } from "../../../shared/lib/documentNumbers";
import { PageHeader, StatCard, T, cardStyle } from "../../../shared/ui/PageShell";
import DateRangeFilter from "../../../shared/ui/DateRangeFilter";
import useTablePagination from "../../../shared/ui/useTablePagination";

const CancellationsList = () => {
  const { t } = useTranslation();
  const { pagination, resetPage } = useTablePagination();
  const currency = readCachedSystemConfig()?.currency_code || "IQD";

  const [range, setRange] = useState(() => resolveDatePreset("today", { withTime: true }));
  const [customerId, setCustomerId] = useState(null);
  const [reason, setReason] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...formatRangeParams(range, { withTime: true, fromKey: "date_from", toKey: "date_to" }),
      };
      if (customerId) params.customer_id = customerId;
      if (reason) params.reason = reason;
      const r = await apiClient.get("/pos/cancellations", { params });
      setData(r.data || null);
    } catch (e) {
      setData(null);
      message.error(e?.response?.data?.message || t("loadError", "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [range, customerId, reason, t]);

  useEffect(() => { load(); resetPage(); }, [load]);
  useEffect(() => {
    apiClient.get("/customers")
      .then((r) => setCustomers(Array.isArray(r.data) ? r.data : (r.data?.data || [])))
      .catch(() => setCustomers([]));
  }, []);

  const reasonOptions = useMemo(() => {
    const set = new Set((data?.by_reason || []).map((r) => r.reason).filter(Boolean));
    return Array.from(set).map((v) => ({ value: v, label: v }));
  }, [data]);

  const customerOptions = useMemo(
    () => customers.map((c) => ({
      value: c.id,
      label: c.phone_number ? `${c.name || `#${c.id}`} (${c.phone_number})` : (c.name || `#${c.id}`),
    })),
    [customers]
  );

  const cancellations = data?.cancellations || [];

  const columns = [
    {
      title: <b>{t("cancelledAt", "Cancelled")}</b>,
      dataIndex: "cancelled_at",
      key: "cancelled_at",
      width: 150,
      render: (v, r) => dayjs(v || r.created_at).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: <b>{t("posReturnsSaleId", "Sale #")}</b>,
      dataIndex: "id",
      key: "id",
      width: 120,
      render: (id, r) => (
        <Link to={`/orders/${id}/view`} style={{ fontWeight: 700, fontFamily: "monospace", fontSize: 12 }}>
          {orderDocNo(r)}
        </Link>
      ),
    },
    {
      title: <b>{t("customer", "Customer")}</b>,
      dataIndex: "customer",
      key: "customer",
      render: (v) => v || <span style={{ color: "#9ca3af" }}>{t("posWalkIn", "Walk-in")}</span>,
    },
    {
      title: <b>{t("paymentMethod", "Payment")}</b>,
      dataIndex: "payment_method",
      key: "payment_method",
      render: (v) => (v === "credit" ? t("posCredit", "Credit") : t("posCash", "Cash")),
    },
    {
      title: <b>{t("total", "Total")}</b>,
      dataIndex: "total_price",
      key: "total_price",
      align: "right",
      render: (v) => <b>{formatCurrency(v, currency)}</b>,
    },
    {
      title: <b>{t("refunded", "Refunded")}</b>,
      dataIndex: "refunded_amount",
      key: "refunded_amount",
      align: "right",
      render: (v) => (
        <span style={{ color: Number(v) > 0 ? T.red : T.sub, fontWeight: 700 }}>
          {formatCurrency(v || 0, currency)}
        </span>
      ),
    },
    {
      title: <b>{t("reason", "Reason")}</b>,
      dataIndex: "cancellation_reason",
      key: "cancellation_reason",
      render: (v) => v || "—",
    },
    {
      title: <b>{t("cancelledBy", "Cancelled by")}</b>,
      dataIndex: "cancelled_by",
      key: "cancelled_by",
      render: (v) => v || "—",
    },
    {
      title: <b>{t("soldBy", "Sold by")}</b>,
      dataIndex: "sold_by",
      key: "sold_by",
      render: (v) => v || "—",
    },
  ];

  return (
    <div className="velan-page" style={{ padding: "clamp(12px, 2.5vw, 24px)", background: T.bg, minHeight: "100%" }}>
      <PageHeader
        breadcrumb={[t("orders", "Orders"), t("posCancellationsTitle", "Cancellations")]}
        title={t("posCancellationsTitle", "Cancellations History")}
        subtitle={t(
          "cancellationsListHint",
          "All cancelled POS sales. To cancel a sale you need Cancel POS Sales permission: open Orders → view a POS sale → Cancel."
        )}
        action={
          <Space>
            <Button icon={<ReloadOutlined />} style={{ borderRadius: 9 }} onClick={load} loading={loading}>
              {t("refresh", "Refresh")}
            </Button>
            <Link to="/returns">
              <Button style={{ borderRadius: 9 }}>{t("posReturnsTitle", "Returns")}</Button>
            </Link>
            <Link to="/orders">
              <Button type="primary" icon={<StopOutlined />} style={{ borderRadius: 9 }}>
                {t("orders", "Orders")}
              </Button>
            </Link>
          </Space>
        }
      />

      <div className="velan-stats-row">
        <StatCard
          icon={<StopOutlined style={{ color: "#ef4444" }} />}
          label={t("posCancellationsTotal", "Total Cancellations")}
          value={data?.total_cancellations ?? 0}
          iconBg="#fee2e2"
        />
        <StatCard
          icon={<StopOutlined style={{ color: "#f59e0b" }} />}
          label={t("posCancellationsSaleAmount", "Cancelled sales amount")}
          value={formatCurrency(data?.total_sale_amount || 0, currency)}
          iconBg="#fef3c7"
        />
        <StatCard
          icon={<StopOutlined style={{ color: "#16a34a" }} />}
          label={t("posOrdersCashBack", "Total Refunded")}
          value={formatCurrency(data?.total_refund || 0, currency)}
          iconBg="#dcfce7"
        />
      </div>

      <div style={{ ...cardStyle, marginBottom: 16, padding: "14px 16px" }}>
        <Space wrap size="middle">
          <DateRangeFilter
            value={range}
            onChange={(v) => setRange(v && v[0] && v[1] ? v : resolveDatePreset("today", { withTime: true }))}
            showTime
            allowClear={false}
            size="small"
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t("customer", "Customer")}
            value={customerId}
            onChange={setCustomerId}
            options={customerOptions}
            style={{ minWidth: 200 }}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t("reason", "Reason")}
            value={reason}
            onChange={setReason}
            options={reasonOptions}
            style={{ minWidth: 180 }}
          />
        </Space>
      </div>

      <div style={cardStyle} className="velan-list-shell">
        <div className="velan-list-body">
          <Table
            size="small"
            loading={loading}
            rowKey="id"
            dataSource={cancellations}
            columns={columns}
            pagination={{ ...pagination, size: "small" }}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: t("noDataFound", "No data found") }}
          />
        </div>
      </div>
    </div>
  );
};

export default CancellationsList;
